import type { VatReturnInputs } from '../types/VatReturn';
import type { ExportConfirmationForm } from './useExportConfirmationForm';
import * as xlsx from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { formatToCurrency, formatToDateString, formatToMonthYear, formatToShortDateString } from '../../../util/formatting';

export function useExportTaxReturn(
	vatReturns: VatReturnInputs,
	exportSettings: ExportConfirmationForm,
) {
	const performExport = async () => {
		await exportToExcel(
			exportSettings.title,
			exportSettings.excelName,
			vatReturns,
		);
	};

	return performExport;
}

const exportToExcel = (
	organisationName: string,
	fileName: string,
	data: VatReturnInputs,
) => {
	const formattedData = formatDataToRows(data, organisationName);

	const workbook = xlsx.utils.book_new();
	const worksheet = xlsx.utils.aoa_to_sheet(formattedData, {
		// skipHeader: true,
	});

	xlsx.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

	workbook.Sheets.Sheet1['!merges'] = getCellsForMerge();
	workbook.Sheets.Sheet1['!cols'] = setCellWidths();
	addCellStyling(workbook.Sheets.Sheet1);

	const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
	const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

	saveAs(blob, `${fileName}.xlsx`);
};

const setCellWidths = () => {
	return [
		{
			wch: 80,
		},
		{ wch: 2 },
		{ wch: 40 },
	]; // A4:B4];
};

const getCellsForMerge = () => {
	return [
		xlsx.utils.decode_range('A1:C1'),
		xlsx.utils.decode_range('A2:C2'),
		xlsx.utils.decode_range('A3:C3'),
		xlsx.utils.decode_range('A4:C4'),
	]; // A4:B4];
};

const addCellStyling = (worksheet: xlsx.WorkSheet) => {
	const getFontBold = () => {
		return {
			font: {
				bold: true,
			},
		};
	};

	const getFontBoldWithUnderline = () => {
		return {
			font: {
				bold: true,
			},
			border: {
				bottom: {
					style: 'thin',
					color: '000000',
				},
			},
		};
	};

	const getFontBoldWithCenter = () => {
		return {
			font: {
				bold: true,
			},
			alignment: { horizontal: 'center' },
		};
	};

	const alignTextEnd = () => {
		return {
			alignment: { horizontal: 'right' },
		};
	};

	worksheet.A1.s = getFontBoldWithCenter();
	worksheet.A2.s = getFontBoldWithCenter();
	worksheet.A3.s = getFontBoldWithCenter();
	worksheet.A6.s = getFontBold();
	worksheet.A15.s = getFontBold();
	worksheet.A20.s = getFontBoldWithUnderline();
	worksheet.B20.s = getFontBoldWithUnderline();
	worksheet.C20.s = getFontBoldWithUnderline();
	worksheet.A22.s = getFontBold();
	worksheet.A26.s = getFontBold();

	worksheet.C7.s = alignTextEnd();
	worksheet.C8.s = alignTextEnd();
	worksheet.C9.s = alignTextEnd();
	worksheet.C10.s = alignTextEnd();
	worksheet.C11.s = alignTextEnd();
	worksheet.C12.s = alignTextEnd();
	worksheet.C13.s = alignTextEnd();
	worksheet.C16.s = alignTextEnd();
	worksheet.C17.s = alignTextEnd();
	worksheet.C18.s = alignTextEnd();
	worksheet.C19.s = alignTextEnd();

	worksheet.C20.s = {
		alignment: { horizontal: 'right' },
		font: {
			bold: true,
		},
		border: {
			bottom: {
				style: 'thin',
				color: '000000',
			},
		},
	};

	worksheet.C23.s = alignTextEnd();
	worksheet.C24.s = alignTextEnd();

	worksheet.C27.s = alignTextEnd();
	worksheet.C28.s = alignTextEnd();
};

const formatDataToRows = (data: VatReturnInputs, organisationName: string) => {
	const rows = [
		['VAT Return'],
		[organisationName],
		[
			`For ${
				// biome-ignore lint/style/noNonNullAssertion: <explanation>
				formatToMonthYear(data.details?.startDate!)
			} to ${
				// biome-ignore lint/style/noNonNullAssertion: <explanation>
				formatToMonthYear(data.details?.endDate!)
			}`,
		],
		[''],
		[''],
		['VAT Return Details'],
		['Registration Number', '', data.details?.registrationNumber.substring(3)],
		['VAT Scheme', '', data.details?.vatScheme],
		['Period covered by this return', '', data.details?.periodType],
		['Late VAT Claims included', '', 'No (Previous period has not been filed)'],
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		['From', '', formatToShortDateString(data.details?.startDate!)],
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		['To', '', formatToShortDateString(data.details?.endDate!)],
		[
			'The deadline for submitting this return online is',
			'',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToShortDateString(data.details?.deadline!),
		],
		[''],
		['VAT Calculations'],
		[
			'VAT Due this period on sales and other outputs',
			'1',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.calculations?.vatDue!),
		],
		[
			'VAT due in this period on acquisitions from other EC Member States',
			'2',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.calculations?.vatDueOnAcquisitions!),
		],
		[
			'Total VAT due (the sum of boxes 1 and 2)',
			'3',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.calculations?.totalVat!),
		],
		[
			'VAT reclaimed in this period on purchases and other inputs (including acquisitions from EC)',
			'4',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.calculations?.vatReclaimed!),
		],
		[
			'VAT to Reclaim from Customs',
			'5',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.calculations?.vatToReclaimFromCustoms!),
		],
		[''],
		['Sales and Purchases Excluding VAT'],
		[
			'Total value of sales and all other outputs excluding VAT (including supplies to EC)',
			'6',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.afterVat?.valueOfSalesExcVat!),
		],
		[
			'Total value of purchases and all other inputs excluding VAT (including acquisitions from EC)',
			'7',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.afterVat?.valueOfPurchasesExcVat!),
		],
		[''],
		['EC Supplies and Purchases Excluding VAT'],
		[
			'Total value of all supplies of goods, excluding any VAT, to other EC Member States',
			'8',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.afterVat?.valueOfSalesExcVatToEc!),
		],
		[
			'Total value of all acquisitions of goods, excluding any VAT, from EC Member States',
			'9',
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			formatToCurrency(data.afterVat?.valueOfPurchasesExcVatToEc!),
		],
	];

	return rows;
};
